import {
  normalizeMedicalInstitutionSpecialties,
  normalizeMedicalInstitutionSpecialtyIds,
  normalizeMedicalInstitutionSpecialtyText,
} from "./medicalInstitutionSpecialties.js";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function splitLegacyFocusLabel(value) {
  const label = String(value || "").replace(/\s+/g, " ").trim();
  // A former draft renderer saved the visible chip string as one value. Split
  // only that explicit legacy delimiter; normal labels are left untouched.
  return label.includes("×")
    ? label.split(/\s*×\s*/).map((item) => item.trim()).filter(Boolean)
    : label ? [label] : [];
}

/**
 * Normalises persisted Focus On values into stable, independently removable
 * records. Canonical values retain their taxonomy ID; custom values retain
 * their entered label and a stable custom ID.
 */
export function normaliseClinicianFocusAreas(items = [], canonicalAreas = []) {
  const catalogue = normalizeMedicalInstitutionSpecialties(canonicalAreas);
  const byId = new Map(catalogue.map((item) => [item.id, item]));
  const byLabel = new Map(catalogue.map((item) => [normalizeMedicalInstitutionSpecialtyText(item.name), item]));
  const seen = new Set();

  return (Array.isArray(items) ? items : []).reduce((records, rawItem) => {
    const rawId = String(typeof rawItem === "object" ? rawItem?.id || "" : rawItem || "").trim();
    const rawLabel = String(typeof rawItem === "object" ? rawItem?.label || rawItem?.name || "" : rawItem || "").trim();
    const declaredCustom = Boolean(typeof rawItem === "object" && rawItem?.isCustom);
    const labelParts = splitLegacyFocusLabel(rawLabel);

    labelParts.forEach((label, index) => {
      const isFirstPart = index === 0;
      const canonical = !declaredCustom && (byId.get(isFirstPart ? rawId : "")
        || byLabel.get(normalizeMedicalInstitutionSpecialtyText(label))
        || (isFirstPart ? byLabel.get(normalizeMedicalInstitutionSpecialtyText(rawId)) : null));
      const record = canonical
        ? { id: canonical.id, label: canonical.name, isCustom: false }
        : label && !UUID_PATTERN.test(label)
          ? { id: `custom:${normalizeMedicalInstitutionSpecialtyText(label)}`, label, isCustom: true }
          : { id: rawId || label, label: "Focus area unavailable", isCustom: false, unresolved: true };
      const key = record.unresolved ? `unresolved:${record.id}` : record.isCustom ? `custom:${normalizeMedicalInstitutionSpecialtyText(record.label)}` : `canonical:${record.id}`;
      if (!record.id || seen.has(key)) return;
      seen.add(key);
      records.push(record);
    });
    return records;
  }, []);
}

export function serialiseClinicianFocusAreas(canonicalIds = [], pickerCustom = [], canonicalAreas = []) {
  const catalogue = normalizeMedicalInstitutionSpecialties(canonicalAreas);
  const canonical = normalizeMedicalInstitutionSpecialtyIds(canonicalIds, canonicalAreas);
  const canonicalLabels = new Set(catalogue.map((item) => normalizeMedicalInstitutionSpecialtyText(item.name)));
  const canonicalRecords = canonical.map((id) => {
    const match = catalogue.find((item) => item.id === id);
    return { id, label: match?.name || "Focus area unavailable", isCustom: !match };
  });
  const seen = new Set(canonicalRecords.map((item) => item.isCustom ? `custom:${normalizeMedicalInstitutionSpecialtyText(item.label)}` : `canonical:${item.id}`));

  return (Array.isArray(pickerCustom) ? pickerCustom : []).reduce((values, item) => {
    const itemId = String(item?.id || "").trim();
    const label = String(item?.label || item?.name || item || "").replace(/\s+/g, " ").trim();
    if (itemId.startsWith("unresolved:")) {
      const unresolvedId = itemId.slice("unresolved:".length);
      if (unresolvedId && !seen.has(`unresolved:${unresolvedId}`)) {
        seen.add(`unresolved:${unresolvedId}`);
        values.push({ id: unresolvedId, label: "Focus area unavailable", isCustom: false, unresolved: true });
      }
      return values;
    }
    const labelKey = normalizeMedicalInstitutionSpecialtyText(label);
    if (!label || canonicalLabels.has(labelKey) || seen.has(`custom:${labelKey}`)) return values;
    seen.add(`custom:${labelKey}`);
    values.push({ id: itemId.startsWith("custom:") ? itemId : `custom:${labelKey}`, label, isCustom: true });
    return values;
  }, canonicalRecords);
}
